/**
 * ElementRenderer — thin dispatcher around per-element render components.
 *
 * The actual JSX for each element type lives in `./renderers.jsx`. This file
 * is responsible for:
 *   1. Computing the shared `spacing` style (margin/padding/outer+inner border)
 *      from element.props once per render.
 *   2. Dispatching by `element.type` to the matching renderer component.
 *   3. Re-exporting `EjsTag` for any external import sites that may exist.
 */
import {
  EjsTag,
  HeadingRender,
  TextRender,
  FooterRender,
  PaymentTermsRender,
  PageNumberRender,
  CompanyLogoRender,
  ImageRender,
  SignatureRender,
  QrCodeRender,
  BarcodeRender,
  InvoiceDetailsRender,
  CustomerInfoRender,
  TotalsRender,
  LineItemsRender,
  SummaryTableRender,
  KeyValueListRender,
  TaxDetailsRender,
  CustomRender,
  TwoColumnRender,
  ThreeColumnRender,
  FourColumnRender,
  VerticalLayoutRender,
  DividerRender,
  SpacerRender,
  PageBreakRender,
  CarriedForwardRender,
  BroughtForwardRender,
} from "./renderers";

export { EjsTag };

/**
 * Per-element-type render map. Each entry is a component receiving
 * `{ p, spacing }` (and optionally `ElementContent` for recursive layouts).
 * Lookup-and-render is O(1) per element vs. the old 25-case switch.
 */
const RENDERERS = {
  heading:        HeadingRender,
  text:           TextRender,
  footer:         FooterRender,
  paymentTerms:   PaymentTermsRender,
  pageNumber:     PageNumberRender,
  companyLogo:    CompanyLogoRender,
  image:          ImageRender,
  signature:      SignatureRender,
  qrCode:         QrCodeRender,
  barcode:        BarcodeRender,
  invoiceDetails: InvoiceDetailsRender,
  customerInfo:   CustomerInfoRender,
  totals:         TotalsRender,
  lineItems:      LineItemsRender,
  summaryTable:   SummaryTableRender,
  keyValueList:   KeyValueListRender,
  taxDetails:     TaxDetailsRender,
  custom:         CustomRender,
  twoColumn:      TwoColumnRender,
  threeColumn:    ThreeColumnRender,
  fourColumn:     FourColumnRender,
  verticalLayout: VerticalLayoutRender,
  divider:        DividerRender,
  spacer:         SpacerRender,
  pageBreak:      PageBreakRender,
  carriedForward: CarriedForwardRender,
  broughtForward: BroughtForwardRender,
};

/** Layout renderers need `ElementContent` to recurse without a module cycle. */
const RECURSIVE_TYPES = new Set(["twoColumn", "threeColumn", "fourColumn", "verticalLayout"]);

function computeSpacing(p) {
  const eb = p.elementBorder;
  const hasInnerBorder = eb && (eb.top || eb.bottom || eb.left || eb.right);
  const innerBorderProps = hasInnerBorder ? {
    borderTop:    eb.top    ? `${eb.top}px ${eb.style || "solid"} ${eb.color || "#000000"}`    : undefined,
    borderBottom: eb.bottom ? `${eb.bottom}px ${eb.style || "solid"} ${eb.color || "#000000"}` : undefined,
    borderLeft:   eb.left   ? `${eb.left}px ${eb.style || "solid"} ${eb.color || "#000000"}`   : undefined,
    borderRight:  eb.right  ? `${eb.right}px ${eb.style || "solid"} ${eb.color || "#000000"}`  : undefined,
    boxSizing:    "border-box",
  } : {};

  return {
    marginTop:     p.marginTop || 0,
    marginBottom:  p.marginBottom || 0,
    paddingTop:    p.paddingTop || 0,
    paddingBottom: p.paddingBottom || 0,
    paddingLeft:   p.paddingLeft || 0,
    paddingRight:  p.paddingRight || 0,
    ...(p.borderWidth > 0 ? {
      border:       `${p.borderWidth}px ${p.borderStyle || "solid"} ${p.borderColor || "#000000"}`,
      borderRadius: p.borderRadius || 0,
    } : {}),
    ...innerBorderProps,
  };
}

export function ElementContent({ element }) {
  const { type, props: p } = element;
  const Renderer = RENDERERS[type];
  if (!Renderer) {
    return <div className="p-2 text-xs text-gray-400">Unknown element: {type}</div>;
  }
  const spacing = computeSpacing(p);
  if (RECURSIVE_TYPES.has(type)) {
    return <Renderer p={p} spacing={spacing} ElementContent={ElementContent} />;
  }
  return <Renderer p={p} spacing={spacing} />;
}
